import { GradientPanel } from '@components/common/GradientPanel';
import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { LANGUAGE_FLAG_EMOJIS } from '@conversed/shared/src/central/constants/languages';
import clsx from 'clsx';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { TTranslationState } from './conversation-review-modal.type';

type Props = {
  displayedQuestion: string;
  translationState: TTranslationState | null;
  showTranslated: boolean;
  isShowingOriginalLanguage: boolean;
  hasMultilingualTrainAI: boolean;
  isDisabled: boolean;
  translateLabel: string;
  onTranslate: () => void;
  // Set only when the question is part of a longer conversation.
  onOpenConversation?: () => void;
};

export const ConversationReviewQuestionSection: React.FC<Props> = ({
  displayedQuestion,
  translationState,
  showTranslated,
  isShowingOriginalLanguage,
  hasMultilingualTrainAI,
  isDisabled,
  translateLabel,
  onTranslate,
  onOpenConversation,
}) => {
  const { t } = useTranslation();
  // Show the toggle in the question section header only when a translation is available.
  // Before that, the initial "Translate" button lives in the card header instead.
  const showToggleButton = hasMultilingualTrainAI && translationState !== null;

  return (
    <div className='conversation-review-modal__section conversation-review-modal__section--question'>
      <div className='conversation-review-modal__section-label'>
        <MaterialIcon
          type='outlined'
          icon='person'
          classes={['conversation-review-modal__section-label-icon']}
        />
        <span className='conversation-review-modal__section-label-text'>
          {t('train-ai.conversation-review.question-label')}
        </span>
        {onOpenConversation && (
          <button
            type='button'
            className='conversation-review-modal__section-label-conversation-link'
            onClick={onOpenConversation}>
            {t('train-ai.conversation-review.thread.link')}
          </button>
        )}
        {showToggleButton && (
          <ButtonT
            type='button'
            styling={
              isShowingOriginalLanguage ? 'outline-amber' : 'outline-blue-400'
            }
            size='timestamp'
            rounding='md'
            disabled={isDisabled}
            onClick={onTranslate}
            className='conversation-review-modal__section-label-translate-button !shadow-none font-semibold'>
            {translateLabel}
          </ButtonT>
        )}
      </div>

      <GradientPanel
        accentColor={isShowingOriginalLanguage ? 'amber' : 'blue'}
        accentSide='left'
        className='conversation-review-modal__question-panel'>
        <p
          className={clsx(
            'conversation-review-modal__question-text',
            isShowingOriginalLanguage &&
              'conversation-review-modal__question-text--original',
          )}>
          {displayedQuestion}
          {showTranslated && translationState && (
            <span className='conversation-review-modal__translation-note ml-2'>
              {' '}
              {LANGUAGE_FLAG_EMOJIS.nl ?? '🇳🇱'}{' '}
              {t('train-ai.conversation-review.language.translated-to-dutch')}
            </span>
          )}
        </p>
      </GradientPanel>
    </div>
  );
};
